/**
 * usePublicEmbedConfig Hook
 *
 * Fetches the PUBLIC embed configuration for an agent (appearance subset only —
 * no allowedDomains, no keys) from GET /api/public/embed-config/:agentId and
 * shares it across all consumers via a module-level cache. Used by the embed
 * chat surface (spec 075 — Phase 2) to apply the widget's configured
 * appearance: header text, primary color theming, and feature toggles.
 *
 * Resilient by design: any fetch failure resolves to `null` config so the
 * widget falls back to platform defaults instead of blocking.
 */

import { useEffect, useState } from 'react'
import { api } from '@/utils/api-client'

/** Published agent embed settings exposed to the widget (whitelisted subset). */
export interface PublicEmbedAgentSettings {
  /** Locales offered by the widget; null/empty = language features off. */
  languages: string[] | null
  /** Show the pre-chat language chooser before the chat initialises. */
  requireLanguageGate: boolean
  /** Localized welcome message per locale. */
  welcomeMessage: Record<string, string> | null
  /** Localized starter prompts per locale. */
  samplePrompts: Record<string, string[]> | null
}

/** Appearance subset returned by the public embed-config endpoint. */
export interface PublicEmbedConfig {
  primaryColor: string | null
  headerText: string | null
  welcomeMessage: string | null
  width: string | null
  height: string | null
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | null
  showBranding: boolean | null
  enableHistory: boolean | null
  enableFileUpload: boolean | null
  /** Published agent's embed settings (spec 075 Phase 3); null when unset. */
  embed?: PublicEmbedAgentSettings | null
  /** Published agent's named Views (spec 075 Phase 5); null when none. */
  views?: import('@taqat/db').AgentViewDefinition[] | null
}

interface CacheEntry {
  config: PublicEmbedConfig | null
  resolved: boolean
  promise: Promise<void> | null
  listeners: Set<() => void>
}

/** Module-level cache: one fetch per agentId per page load. */
const cache = new Map<string, CacheEntry>()

function getEntry(agentId: string): CacheEntry {
  let entry = cache.get(agentId)
  if (!entry) {
    entry = { config: null, resolved: false, promise: null, listeners: new Set() }
    cache.set(agentId, entry)
  }
  return entry
}

function ensureFetch(agentId: string): void {
  const entry = getEntry(agentId)
  if (entry.resolved || entry.promise) return
  entry.promise = api
    .get(`/public/embed-config/${agentId}`, {
      requiresAuth: false,
      showErrorToast: false,
    })
    .then((res: { success?: boolean; data?: unknown }) => {
      // The route nests its payload: { success, data: { data: config, message } }.
      // Unwrap defensively so a future flattening of the response keeps working.
      const payload = res?.data as
        | (PublicEmbedConfig & { data?: never })
        | { data?: PublicEmbedConfig }
        | null
        | undefined
      entry.config =
        payload && 'primaryColor' in payload
          ? (payload as PublicEmbedConfig)
          : ((payload as { data?: PublicEmbedConfig } | null | undefined)?.data ?? null)
    })
    .catch(() => {
      // Missing config / not published / network error → platform defaults.
      entry.config = null
    })
    .finally(() => {
      entry.resolved = true
      entry.promise = null
      for (const l of entry.listeners) l()
    })
}

/**
 * Subscribes to the public embed config for an agent.
 *
 * @param agentId - Agent whose embed appearance to load
 * @param enabled - Only fetch when true (pass `isEmbedMode` — the config is
 *                  irrelevant outside the embedded widget)
 * @returns `{ config, resolved }` — config is null until resolved, and stays
 *          null when no embed config exists (defaults apply)
 */
export function usePublicEmbedConfig(
  agentId: string | null | undefined,
  enabled: boolean
): { config: PublicEmbedConfig | null; resolved: boolean } {
  const [, forceRender] = useState(0)

  useEffect(() => {
    if (!enabled || !agentId) return
    const entry = getEntry(agentId)
    const listener = () => forceRender((n) => n + 1)
    entry.listeners.add(listener)
    ensureFetch(agentId)
    return () => {
      entry.listeners.delete(listener)
    }
  }, [agentId, enabled])

  if (!enabled || !agentId) return { config: null, resolved: true }
  const entry = getEntry(agentId)
  return { config: entry.config, resolved: entry.resolved }
}

/**
 * Converts a hex color ("#F7941D") to the HSL triple format the app theme
 * uses for CSS variables ("33 93% 54%"). Returns null for invalid input.
 */
export function hexToHslTriple(hex: string): string | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const n = parseInt(match[1], 16)
  const r = ((n >> 16) & 0xff) / 255
  const g = ((n >> 8) & 0xff) / 255
  const b = (n & 0xff) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/**
 * Applies the embed config's primary color to the iframe document by
 * overriding the app's `--primary` CSS variable — scoped to embed mode only:
 * this runs inside the embed iframe's own document, so the main app is
 * structurally unaffected. No-op for missing/invalid colors (defaults stay).
 */
export function applyEmbedPrimaryColor(primaryColor: string | null | undefined): void {
  if (!primaryColor) return
  const triple = hexToHslTriple(primaryColor)
  if (!triple) return
  document.documentElement.style.setProperty('--primary', triple)
  document.documentElement.style.setProperty('--ring', triple)
}
